import { type ReactNode, useEffect, useMemo, useRef, useState } from "react";

import { zodResolver } from "@hookform/resolvers/zod";
import {
  Button,
  Icon_CheckmarkFilled60,
  InfoBar,
  isTouchDevice,
  Link,
  LogSentryException,
  Modal,
  ServiceError,
  TextField,
} from "@shapr3d/frontend-lib";
import { useMutation } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { z } from "zod";

import { eduLicenseInfoURL, queryKeys } from "../../config";
import useAccount from "../../hooks/useAccount";
import { useTracker } from "../../hooks/useTracker";
import useTranslation from "../../hooks/useTranslation";
import { ElementClickAction, ElementClickLocation } from "../../lib/TrackingAdapter";
import { UMSrequestClient } from "../../stores/requestClient";
import { ServiceErrorCode, SubscriptionType } from "../../types";
import { isEmailInvalid } from "../../utils/validationUtils";
import ReCAPTCHA, { type ReCAPTCHAAPI } from "../ReCAPTCHA/ReCAPTCHA";

import style from "./EmailChangeModal.module.css";

export interface EmailChangeFormData {
  newEmail: string;
  password: string;
  captcha: string;
}
export default function EmailChangeModal({
  onClose,
  open,
}: {
  onClose: () => void;
  open: boolean;
}) {
  const { t, m } = useTranslation();
  const tracker = useTracker();

  const schema = useMemo(
    () =>
      z.object({
        newEmail: z
          .string()
          .nonempty(t(m.Account.EmailChange.ValidationNewEmail))
          .refine(
            (value) => !isEmailInvalid(value),
            t(m.Account.EmailChange.ValidationInvalidEmail)
          ),
        password: z.string().nonempty(t(m.Account.EmailChange.ValidationPassword)).max(72),
      }),
    [t, m]
  );

  const {
    register,
    handleSubmit,
    getValues,
    formState: { errors, dirtyFields, isSubmitting },
    reset,
  } = useForm<EmailChangeFormData>({
    resolver: zodResolver(schema),
    defaultValues: {
      newEmail: "",
      password: "",
      captcha: "",
    },
  });

  useEffect(() => {
    if (open) {
      reset({
        newEmail: "",
        password: "",
        captcha: "",
      });
    }
  }, [open, reset]);
  const { mutateAsync, error, isSuccess } = useMutation({
    mutationKey: [...queryKeys.authenticatedQueryKey, "request-change-email"],
    mutationFn: ({
      newEmail,
      password,
      captcha,
    }: {
      newEmail: string;
      password: string;
      captcha: string;
    }) =>
      UMSrequestClient.authenticatedRequest({
        method: "POST",
        url: `/change-email`,
        body: { newEmail, password, captcha },
      }),
  });
  const [displayCaptchaError, setDisplayCaptchaError] = useState(false);
  const recaptchaRef = useRef<ReCAPTCHAAPI>(null);
  const { data: accountData } = useAccount();

  const submit = async (data: EmailChangeFormData) => {
    try {
      const recaptchaValue = await recaptchaRef?.current?.execute();
      try {
        if (recaptchaValue) {
          tracker.trackButtonClick({
            location: ElementClickLocation.CHANGE_EMAIL_MODAL,
            action: ElementClickAction.CONFIRM,
          });
          await mutateAsync({ ...data, captcha: recaptchaValue });
        }
      } catch (error) {
        // This is to not throw Unhandled Promise Rejection error
      }
    } catch (error) {
      setDisplayCaptchaError(true);
      tracker.trackEmailChangeModalError(error);
      LogSentryException({ messagePrefix: "Captcha error", error });
    }
  };

  let errorMessage = null;
  if (error) {
    if (error instanceof ServiceError && error.hasCode(ServiceErrorCode.WrongPassword)) {
      errorMessage = t(m.Account.EmailChange.IncorrectPassword);
    } else if (error instanceof ServiceError && error.hasCode(ServiceErrorCode.InvalidEmail)) {
      errorMessage = t(m.ErrorMessage.InvalidEmail);
    } else if (error instanceof ServiceError && error.hasCode(ServiceErrorCode.EmailAlreadyUsed)) {
      errorMessage = t(m.ErrorMessage.EmailAlreadyUsed);
    } else if (error instanceof ServiceError && error.hasCode(ServiceErrorCode.CaptchaMissmatch)) {
      errorMessage = t(m.ErrorMessage.CaptchaError);
    } else {
      errorMessage = t(m.ErrorMessage.Unknown);
    }
  }
  if (displayCaptchaError) {
    errorMessage = t(m.ErrorMessage.CaptchaError);
  }

  const isInputsDirty = dirtyFields.newEmail && dirtyFields.password;

  return (
    <Modal
      open={open}
      title={t(m.Account.EmailChange.Title)}
      size="small"
      onClose={() => {
        onClose();
      }}
    >
      {!isSuccess && (
        <form className={style.emailChangeForm} onSubmit={handleSubmit(submit)}>
          {accountData?.subscriptions.some((sub) => sub.type === SubscriptionType.Edu) && (
            <InfoBar
              variant="critical"
              text={
                <>
                  Note, that upon confirmation of your new email, you will lose your current Edu
                  license. If your new email is eligible, please{" "}
                  <Link href={eduLicenseInfoURL} target="_blank">
                    resubmit it for approval
                  </Link>
                  .
                </>
              }
            />
          )}
          <TextField
            placeholder={t(m.Account.EmailChange.NewEmailPlaceholder)}
            {...register("newEmail")}
            errorMessage={errors.newEmail?.message}
            autoFocus={!isTouchDevice()}
          ></TextField>
          <TextField
            type="password"
            placeholder={t(m.Account.EmailChange.PasswordPlaceholder)}
            {...register("password")}
            errorMessage={errors.password?.message}
          ></TextField>
          <div>{t(m.Account.EmailChange.ConfirmationInfo)}</div>
          <Button type="submit" disabled={!isInputsDirty || isSubmitting}>
            {t(m.Account.EmailChange.SendConfirmationLink)}
          </Button>
          <ReCAPTCHA ref={recaptchaRef} />
          {errorMessage && <div className={style.error}>{errorMessage}</div>}
        </form>
      )}
      {isSuccess && (
        <div className={style.emailChangeSuccessContainer}>
          <Icon_CheckmarkFilled60 className={style.image} />
          <div className={style.messageContainer}>
            <span>{t(m.Account.EmailChange.CheckMailbox)}</span>
            <span>
              {t(m.Account.EmailChange.ConfirmationSent, {
                emailAddress: getValues("newEmail"),
                email: (chunks: ReactNode) => <span className={style.newEmailText}>{chunks}</span>,
              })}
            </span>
          </div>
          <Button
            variant="grayscale"
            onClick={() => {
              onClose();
            }}
          >
            {t(m.Account.EmailChange.Close)}
          </Button>
        </div>
      )}
    </Modal>
  );
}
